import type { EventInput } from '@fullcalendar/core';
import { format } from 'date-fns';
import {
  daysRange,
  formatIsoDate,
  isSameDate,
} from '@src/shared/lib';
import type {
  Absence,
  AbsenceDto,
  AbsenceType,
  AbsenceTypeDto,
  ActivityTypeGroup,
  ActivityTypeGroupDto,
  SelectedAbsence,
  TimelineSlot,
} from '../types';
import { userResourceId } from './resources';

export function selectAbsences(data: Array<AbsenceDto>) {
  return data.map(absenceFromDto);
}

function absenceFromDto(absenceDto: AbsenceDto): Absence {
  return {
    id: absenceDto.id,
    userId: absenceDto.acti_user,
    typeId: absenceDto.acti_type,
    colourFg: absenceDto.acti_colour_fg,
    colourBg: absenceDto.acti_colour_bg,
    date: new Date(absenceDto.acti_date),
    dateStr: absenceDto.acti_date,
    startTime: toAbsenceTime(absenceDto.acti_start_time),
    endTime: toAbsenceTime(absenceDto.acti_end_time),
    absenceTentative: absenceDto.acti_tentative,
    allDay: absenceDto.all_day,
    approvedManager: absenceDto.acab_approved_manager,
    approvedHr: absenceDto.acab_approved_hr,
    notes: absenceDto.acab_notes,
    export: absenceDto.acab_export,
    blockPosition: absenceDto.block_position,
    workdays: absenceDto.workday_count,
    showWorkdays: absenceDto.show_workday_count,
  };
}

function toAbsenceTime(time: string | null) {
  if (!time) return null;
  return time.slice(0, 5); // no seconds
}

function toCalendarDate(value: Date | string): Date {
  if (value instanceof Date) {
    return value;
  }

  const isoDate = formatIsoDate(value);
  return new Date(`${isoDate}T00:00:00`);
}

export function getEventLabel(
  start: Date | string,
  end: Date | string,
  dayView = false,
) {
  const startDate = toCalendarDate(start);
  const endDate = toCalendarDate(end);

  if (!dayView) return startDate.getDate();
  if (isSameDate(startDate, endDate)) {
    return format(startDate, 'dd.MM');
  } else {
    return `${format(startDate, 'dd.MM')} – ${format(endDate, 'dd.MM')}`;
  }
}

export function formatDurationLabel(startTime: string, endTime: string) {
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);

  let totalMinutes =
    endHours * 60 + endMinutes - (startHours * 60 + startMinutes);

  if (totalMinutes < 0) {
    totalMinutes += 24 * 60;
  }

  if (totalMinutes < 60) {
    return '';
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = String(totalMinutes % 60).padStart(2, '0');

  return `${hours}:${minutes}`;
}

export function absencesToEvents(
  slot: TimelineSlot | null,
  absences: Absence[],
  dayView: boolean = false,
): EventInput[] {
  if (!absences?.length) {
    return [];
  }

  function eventDuration(absence: Absence) {
    let duration = {
      start: formatIsoDate(absence.date),
      end: formatIsoDate(absence.date),
      allDay: absence.allDay,
    };
    if (absence.startTime) {
      duration = {
        ...duration,
        start: formatIsoDate(absence.date, absence.startTime),
      };
    }
    if (absence.endTime) {
      duration = {
        ...duration,
        end: formatIsoDate(absence.date, absence.endTime),
      };
    }
    const slotDuration = getSelectedAbsenceDuration(slot, absence);
    if (slotDuration) {
      duration = {
        ...duration,
        ...slotDuration,
      };
    }
    return duration;
  }

  function getRenderEventId(absence: Absence) {
    const viewPrefix = dayView ? 'day' : 'month';
    const neutralSuffix = absence.useNeutralColour ? '-neutral' : '';
    const typeSuffix = absence.typeId != null ? `-t${absence.typeId}` : '';

    return `${viewPrefix}-${absence.id}${neutralSuffix}${typeSuffix}`;
  }

  return absences.map((a) => {
    const slotDuration = getSelectedAbsenceDuration(slot, a);
    const effectiveStartTime = slotDuration ? slot?.startTime : a.startTime;
    const effectiveEndTime = slotDuration ? slot?.endTime : a.endTime;
    const label =
      dayView && !a.allDay && effectiveStartTime && effectiveEndTime
        ? formatDurationLabel(effectiveStartTime, effectiveEndTime)
        : null;

    return {
      id: getRenderEventId(a),
      absenceId: a.id,
      resourceId: userResourceId(a.userId),
      userId: a.userId,
      type: a.typeId,
      neutralColour: a.useNeutralColour ?? false,
      block_position: a.blockPosition,
      workday_count: a.workdays,
      show_workday_count: a.showWorkdays,
      approvedManager: a.approvedManager,
      approvedHr: a.approvedHr,
      tentative: a.absenceTentative ?? false,
      editalbe: false,
      colourFg: a.colourFg,
      colourBg: a.colourBg,
      ...eventDuration(a),
      ...(dayView && {
        resourceEditable: false,
        startEditable: false,
        durationEditable: true,
        extendedProps: {
          ...(label && { label }),
        },
      }),
    };
  });
}

export function absenceSelectionToSlot(data: SelectedAbsence): TimelineSlot {
  return {
    start: data.start,
    end: data.end,
    startTime: data.startTime,
    endTime: data.endTime,
    selectedIds: data.absenceIds,
    editedAbsenceId: data.absenceIds.length === 1 ? data.absenceIds[0] : null,
    editedAbsenceIds: data.absenceIds,
    resourceId: userResourceId(data.userId),
    userId: data.userId,
  };
}

export function groupAbsencesByUserDate(
  absences: Absence[],
): Map<string, Absence[]> {
  const grouped = new Map<string, Absence[]>();
  for (const absence of absences) {
    const key = `${absence.userId}:${absence.dateStr}`;
    const group = grouped.get(key);
    if (group) {
      group.push(absence);
    } else {
      grouped.set(key, [absence]);
    }
  }
  return grouped;
}

export function hasMixedHourlyTypes(group: Absence[]): boolean {
  if (group.length <= 1 || group[0].allDay) return false;
  const typeIds = new Set(group.map((a) => a.typeId));
  return typeIds.size > 1;
}

export function getUserAbsences(userId: number | null, absences: Absence[]) {
  if (!userId || !absences || !absences.length) {
    return [];
  }
  return absences.filter((absence) => absence.userId === userId);
}

type AbsencePayloadInput = {
  startTime?: string | null;
  endTime?: string | null;
  dateFrom?: Date | string | null;
  dateTo?: Date | string | null;
  [key: string]: unknown;
};

export function prepareAbsencePayload(values: AbsencePayloadInput) {
  const { dateFrom, dateTo, startTime, endTime, absenceId } = values;
  return {
    acti_user: values.userId,
    date_from: dateFrom ? formatIsoDate(dateFrom) : null,
    date_to: dateTo ? formatIsoDate(dateTo) : null,
    ...(startTime ? { acti_start_time: startTime } : {}),
    ...(endTime ? { acti_end_time: endTime } : {}),
    ...(typeof absenceId === 'number' ? { absence_id: absenceId } : {}),
    acti_type: values.typeId,
    acti_tentative: values.tentative,
    acab_notes: values.notes,
  };
}

type GetAbsenceIdsForRangeOptions = {
  dayView?: boolean;
  startTime?: string | null;
  endTime?: string | null;
  excludeIds?: number[];
};

function overlapsTimedSelection(
  absence: Absence,
  startTime: string | null | undefined,
  endTime: string | null | undefined,
) {
  if (absence.allDay || !absence.startTime || !absence.endTime) {
    return true;
  }

  if (!startTime || !endTime) {
    return true;
  }

  return absence.startTime < endTime && startTime < absence.endTime;
}

export function doesTimedRangeOverlapAbsences(
  absences: Absence[],
  date: Date | string,
  startTime: string | null | undefined,
  endTime: string | null | undefined,
  excludeIds: number[] = [],
) {
  if (!startTime || !endTime) {
    return false;
  }

  const day = formatIsoDate(date);

  return absences.some(
    (absence) =>
      absence.dateStr === day &&
      !excludeIds.includes(absence.id) &&
      overlapsTimedSelection(absence, startTime, endTime),
  );
}

export function getAbsencesIdsForRange(
  dateFrom: Date | string,
  dateTo: Date | string,
  absences: Absence[],
  options: GetAbsenceIdsForRangeOptions = {},
): number[] {
  const {
    dayView = false,
    startTime = null,
    endTime = null,
    excludeIds = [],
  } = options;

  if (dayView) {
    const day = formatIsoDate(dateFrom);
    return absences
      .filter(
        (absence) =>
          absence.dateStr === day &&
          !excludeIds.includes(absence.id) &&
          overlapsTimedSelection(absence, startTime, endTime),
      )
      .map((absence) => absence.id);
  }

  const range = daysRange(toCalendarDate(dateFrom), toCalendarDate(dateTo)).map(
    (d) => formatIsoDate(d),
  );
  return absences
    .filter((absence) => !excludeIds.includes(absence.id))
    .filter((absence) => range.includes(absence.dateStr))
    .map((absence) => absence.id);
}

export function getEventTimeDelta(info: { milliseconds: number }) {
  if (info.milliseconds !== 0) {
    return info.milliseconds / 1000 / 60;
  }
  return 0;
}

export function shiftTime(time: string, deltaMinutes: number) {
  const [h, m] = time.split(':').map(Number);
  const d = new Date(1970, 0, 1, h, m, 0, 0);
  d.setMinutes(d.getMinutes() + deltaMinutes);
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function getSelectedAbsenceDuration(
  slot: TimelineSlot | null,
  absence: Absence,
) {
  const activeAbsenceId =
    slot?.editedAbsenceId ??
    (slot?.selectedIds?.length === 1 ? slot.selectedIds[0] : null);

  if (
    !slot ||
    !slot.start ||
    !slot.end ||
    activeAbsenceId == null ||
    activeAbsenceId !== absence.id
  ) {
    return null;
  }
  if (!slot.startTime && !slot.endTime) {
    return null;
  }

  return {
    allDay: false,
    ...(slot.startTime && {
      start: formatIsoDate(slot.start, slot.startTime),
    }),
    ...(slot.endTime && { end: formatIsoDate(slot.start, slot.endTime) }),
  };
}

export function selectAbsenceTypes(data: Array<AbsenceTypeDto>) {
  return data.map(absenceTypeFromDto);
}
function absenceTypeFromDto(absenceTypeDto: AbsenceTypeDto): AbsenceType {
  return {
    id: absenceTypeDto.id,
    name: absenceTypeDto.acty_name,
    groupId: absenceTypeDto.acty_group,
    tentativeable: absenceTypeDto.acty_tentativeable,
    approvableManager: absenceTypeDto.acty_approvable_manager,
    approvableHr: absenceTypeDto.acty_approvable_hr,
    backdateable: absenceTypeDto.acty_backdateable,
  };
}

export function selectActivityTypeGroups(
  data: Array<ActivityTypeGroupDto>,
): Array<ActivityTypeGroup> {
  return data.map(activityTypeGroupFromDto);
}

function activityTypeGroupFromDto(
  dto: ActivityTypeGroupDto,
): ActivityTypeGroup {
  return {
    id: dto.id,
    name: dto.actg_name,
    colourFg: dto.actg_colour_fg,
    colourBg: dto.actg_colour_bg,
  };
}
